import React, { PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Text, capitalizeFirstLetter, useIsInIframe, useWasOpenedByWindowOpen } from 'shared-components';
import { CloseWindowOnSuccessButton } from '@components/_shared/AppBar/CloseWindowOnSuccessButton';
import { CloseIframeOnSuccessButton } from '@components/_shared/AppBar/CloseIframeOnSuccessButton';
import { PlaidConnectCardContainer } from '@components/Apply/BankConnect/PlaidConnectCard';
import { setTemp } from '@store/actions';
import { useHistory } from 'react-router-dom';
import { useApplyWizardContext } from '../../context/ApplyWizardContext';
import { BCMShortWizardShell } from '@components/BCMShort/WizardShell';

const EndAsWillContact = (
  props: PropsWithChildren<{
    theme: any;
    first?: string;
    businessName?: string;
    match?: any;
    business_id: string;
    user: RootState['user'];
    vendor_name: string;
    owner1First?: string;
    loan_amount: string;
  }>
) => {
  const isOpenedInWindow = useWasOpenedByWindowOpen();
  const isOpenedInIframe = useIsInIframe();

  const history = useHistory();
  const { nextRoute, dataLayerPush } = useApplyWizardContext();

  const displayFirstName = props.first ? ` ${capitalizeFirstLetter(props.first)}` : '';

  return (
    <BCMShortWizardShell currentStepIndex={2}>
      <Helmet>
        <title>Your Business Credit Score is Processing</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          You&apos;re all set
        </Text>
      </div>

      <div className="flex flex-col gap-[20px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            We&apos;ll be in touch soon
          </Text>
        </div>

        <Text variant="body-general" className="!text-[16px] !leading-[24px] text-center max-w-[562px]">
          You&apos;re all set{displayFirstName}! We&apos;re pulling everything together on our end, and will notify you
          in a few business days when your {props.businessName ? `${props.businessName}` : ''} Business Credit
          Monitoring is ready for action.
        </Text>

        <CloseWindowOnSuccessButton />
        <CloseIframeOnSuccessButton />

        {!isOpenedInWindow && !isOpenedInIframe && (
          <div className="w-full pt-[20px] flex justify-center">
            <PlaidConnectCardContainer
              {...{
                user: props.user,
                history,
                vendor_name: props.vendor_name,
                loan_id: undefined,
                loan_amount: props.loan_amount,
                owner1First: props.owner1First,
                dataLayerPush,
                business_id: props.business_id,
                first: props.owner1First,
                setTemp,
                match: props.match,
                nextRoute,
                customConnectEndpoint: undefined,
                customLinkTokenEndpoint: undefined,
                customOnConnect: () => {},
                textConnectWithPlaidTextBody:
                  'Enhance the data used in your Business Credit Monitoring, with our trusted partner Plaid.',
                textConnectWithPlaidTextSuccessBody:
                  'Enhance the data used in your Business Credit Monitoring, with our trusted partner Plaid.',
                allowSkipConnect: false,
                section: 'bcm-short-end-as-will-contact',
              }}
            />
          </div>
        )}
      </div>
    </BCMShortWizardShell>
  );
};

const selector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    first: selector(state, 'owner_1_first'),
    businessName: selector(state, 'business_name'),
    theme: state.theme,
    business_id: selector(state, 'business_id'),
    user: state.user,
    vendor_name: state.brand?.data?.vendor_name,
    owner1First: selector(state, 'owner_1_first'),
    loan_amount: selector(state, 'loan_amount'),
  };
};

export const BCMShortEndAsWillContact = connect(mapStateToProps)(EndAsWillContact);
